import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { observable, Observable, Subject, Subscriber } from 'rxjs';

import { ApiResponse } from './api.models';
import { ShellProfile } from './../../model/profile';
import { ProfileCreateRS, ReadProfileDetailRS, ReadProfileGenderRS } from './api-response';
import { ApiPartialResendVerificationActiveAccountRQ, ApiUserResetPasswordRequestRQ, ApiUserVerificationCodeRQ } from './api-request';
import { IAppConfig } from '../config/app-config.model';
import { APP_CONFIG } from '../config/app-config.service';
import { KeycloakService } from 'keycloak-angular';
import * as uuid from 'uuid';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { TOAST_FOREVER, UiService } from '../ui.service';
import { LogService } from '../log.service';
import { LanguageService } from '../language.service';
import { XnStorageService } from '../xn-storage';
import { AuthService as OktaService } from '@auth0/auth0-angular';

export const AUTH_KEY = 'AUTH';

export interface TokenRS extends ApiResponse{
  expiresInSeconds? : number;
  department?: string;
  domainCode?: string;
  grantedPermissions?: string;
  isAdminUser?: boolean;
  profileId?: string;
  requireChangePw?: boolean;
  role?: string;
  userName?: string;
  errorsCode?: string;
  firstName?: string
  lastName?: string;
  locale?: string,
}

export interface AuthState {
  isAuthenticated: boolean;
  expiredIn?: number;
  expiredAt?:any;
  profileId?: string;
  userName?: string;
  isAdminUser?: boolean;
  department?: string;
  role?: string;
  grantedClaims?: string[];
  domainCode?: string;
  requireChangePw: boolean;
  errorsCode?: string;
  firstName?: string,
  lastName?: string,
  locale?: string,
}


export const initialState: AuthState = {
  isAuthenticated: false,
  expiredIn: 0,
  expiredAt: new Date(),
  profileId: '',
  userName: '',
  isAdminUser: false,
  department: '',
  role: '',
  grantedClaims: [],
  domainCode: '',
  requireChangePw: false,
  errorsCode: ''
};

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  protected isAuthenticated = false;

  constructor(
    private httpClient: HttpClient,
    private localStorageService: XnStorageService,
    @Inject(APP_CONFIG) protected config: IAppConfig,
    private keycloak: KeycloakService,
    private translate: TranslateService,
    private router: Router,
    private ui: UiService,
    private log: LogService,
    private language: LanguageService,
    private oktaService: OktaService,
  ) { }

  private baseAuthApiUrl = 'WBEAuthentication';
  private baseProfileApiUrl = 'ResidentPortalProfile';
  private profileSubject = new Subject<string>();
  private _activateAccountSB = new Subject();

  private pingTimer: any = null;
  private _lastLoginStatus: boolean = null;

  private _idpToken: string = '';

  set idpToken(value: string){
    this._idpToken = value;
  }

  get idpToken(): string{
    return this._idpToken;
  }
  
  post<T>(path: string, body?: any): Observable<T>{
    let headers = new HttpHeaders();

    this.isAuthenticated = this.isLoggedIn();

    headers = headers.append('verify-session', `${this.isAuthenticated}`);

    if(this.config.isUsingExternalIdentityServer && this.idpToken){
      headers = headers.append(`Authorization`, `Bearer ${this.idpToken}`);
    }
    return this.httpClient.post<T>(path, body,{headers: headers});
  }

  revokeToken(): Observable<ApiResponse> {
    return this.post<ApiResponse>(this.baseAuthApiUrl + '/revoke-token');
  }

  userResetPassword(request: ApiUserVerificationCodeRQ): Observable<ApiResponse> {
    return this.post('ResidentPortalUser/ResetPassword', request);
  }

  resendVerificationEmail(verifyUserAccountLink: string) : Observable<ApiResponse> {
    var apiPartialResendVerificationActiveAccountRQ : ApiPartialResendVerificationActiveAccountRQ = {
      verifyUserAccountLink: verifyUserAccountLink
    }
    var actionUrl = `ResidentPortalUser/ReSendVerifyUserAccountEmail`;
    return this.post(actionUrl, apiPartialResendVerificationActiveAccountRQ);
  }

  login(userName: string, password: string): Observable<AuthState>{
    return new Observable((observable) => {
      const body = {
        username: userName,
        password: password,
        isAdminUser: false
      };
      
      this.post<TokenRS>(this.baseAuthApiUrl + `/token`, body).subscribe({
        next: (tokenRes) => {
          this.handleApiLoginSuccess(observable, tokenRes);
        },
        error: (err) => {
          observable.error(err);
        }
      });
    })
  }

  private handleApiLoginSuccess(observable: Subscriber<AuthState>, tokenRes: TokenRS){
    this._shown409Error = false;
    if (tokenRes.success) {
      if(!tokenRes.profileId){
        this.translate.get('common.error.general').subscribe(value => {
          observable.error({message: value});
        })
        return;
      }

      let arrPermissions: string[] = [];

      if (tokenRes.grantedPermissions) {
        arrPermissions = tokenRes.grantedPermissions.split(';');
      }

      const now = new Date();
      const expiredAt: any = now.setSeconds(now.getSeconds() + tokenRes.expiresInSeconds);

      const authState: AuthState = {
        isAuthenticated: tokenRes.requireChangePw ? false : true,
        expiredIn: tokenRes.expiresInSeconds,
        expiredAt: expiredAt,
        profileId: tokenRes.profileId,
        userName: tokenRes.userName,
        isAdminUser: tokenRes.isAdminUser,
        department: tokenRes.department,
        role: tokenRes.role,
        grantedClaims: arrPermissions,
        domainCode: tokenRes.domainCode,
        requireChangePw: tokenRes.requireChangePw,
        firstName: tokenRes.firstName,
        lastName: tokenRes.lastName,
        // userLogonID: tokenRes.userLogonID,
        locale: tokenRes.locale
      };

      if(tokenRes.locale){
        this.language.setLanguage(tokenRes.locale, true);
      }
      
      if (tokenRes.requireChangePw) {
        this.loginSuccess(authState);
        observable.next(authState);
      } else {
        this.loginSuccess(authState);
        observable.next(authState);
      }
    } else {
      if(tokenRes?.errors[0]?.code == "XWAPI030"){
        const authState: AuthState = {
          isAuthenticated: false,
          expiredIn: tokenRes.expiresInSeconds,
          profileId: tokenRes.profileId,
          userName: tokenRes.userName,
          requireChangePw: tokenRes.requireChangePw,
          errorsCode: tokenRes?.errors[0]?.code
        };
        observable.next(authState);
      }
      else if(tokenRes?.errors[0]?.code == "XWAPI052" || tokenRes?.errors[0]?.code == "PS0011"){
        if(this.config.isUsingExternalIdentityServer){
          setTimeout(() => {
            this.forceLogoutFromIDP();
          }, 3000);
        }
      }
      else{
        observable.next(null);
      }
    }
  }

  private loginSuccess(auth: AuthState){
    this._shown409Error = false;
    this.localStorageService.setItem(AUTH_KEY, auth);
    this.setProfileId(auth.profileId);

    // start timer
    this.monitor();
  }

  pingIntervalTimeInSeconds = 5;
  startPingTimer(){
    this.stopPingTimer();

    this.pingTimer = setInterval(this.handlePingTimer, this.pingIntervalTimeInSeconds * 1000);
  }

  monitor(){
    if(this.isLoggedIn()){
      this.startPingTimer();
    }

    // listen localstorage event change
    window.removeEventListener('storage', this._onStorageChanged);
    window.addEventListener('storage', this._onStorageChanged);
  }

  private getAuthStorageKey(): string{
    return `${this.config.storeKey}${AUTH_KEY}`;
  }

  private _onStorageChanged = (event: StorageEvent) => {
    if(!event) return;

    if(event.key === this.getAuthStorageKey()){
      this.log.log(`on storage auth changed`);
      this.log.log(event);

      if(event.oldValue && event.newValue){
        let oldState: AuthState, newState: AuthState;
        try {
          oldState = JSON.parse(event.oldValue);
          newState = JSON.parse(event.newValue);
        } catch (error: any) {
          this.log.log(`Failed to parse auth state ${error?.message}`);
        }

        if(oldState && this.isValidAuthData(oldState) && (!newState || !this.isValidAuthData(newState)) ){
          this.log.log(`Storage auth changed => user is logged out.`);
          window.location.reload();
        }
        else if(newState && this.isValidAuthData(newState) && (!oldState || !this.isValidAuthData(oldState))){
          this.log.log(`Storage auth changed => user is logged in.`);
          window.location.reload();
        }
      }
    }
  }

  handlePingTimer = () => {
    const isLoggedIn = this.isLoggedIn();
    this.log.log(`auth isLoggedIn: ${isLoggedIn}`);
    if(this._lastLoginStatus && !isLoggedIn){
      // user logged out
      this.log.log(`User is logged out.`);
      this.forceLogout();
    }

    this._lastLoginStatus = isLoggedIn;
  }

  stopPingTimer(){
    if(this.pingTimer != null){
      window.clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  getAuthorizationToken(): string {
    return 'Bearer ';
  }

  relogin(userName: string, password: string): Observable<AuthState>{
    return new Observable((observable) => {
      if(this.isLoggedIn()){
        this.logout(false, (status) => {
          this.login(userName, password).subscribe({
            next: (auth) => {
              observable.next(auth);
            },
            error: (err) => {
              observable.error(err);
            }
          });
        })
        return;
      }

      this.login(userName, password).subscribe({
        next: observable.next,
        error: observable.error
      });
    })
  }

  setProfileId(profileId: string){
    this.profileSubject.next(profileId);
  }

  getDisplayName(){
    if(!this.isLoggedIn()) return '';

    const auth = this.getAuth();
    if(!auth) return '';

    if(auth.lastName && auth.firstName){
      return `${auth.firstName} ${auth.lastName}`;
    }

    return auth.firstName || auth.userName;
  }

  getProfileId() : Observable<string>{
    return this.profileSubject.asObservable();
  }

  getAuth(): AuthState{
    const auth: AuthState = this.localStorageService.getItem(AUTH_KEY);
    return auth;
  }
  
  getProfileIdValue(): string{
    if(!this.isLoggedIn()) return null;

    const auth = this.getAuth();
    if(!auth || !auth.isAuthenticated) return "";
    return auth.profileId;
  }

  showSessionExpiredMessage(){
    this.ui.toastT('common.error.session-expired', {duration: 5000});
  }

  handleAuthSessionExpired(){
    // clear data
    this.forceLogout();

    this.showSessionExpiredMessage();
    
    setTimeout(() => {
      if(this.config.isUsingExternalIdentityServer){
        // force logout from IDP
        this.forceLogoutFromIDP();
      }
      else{
        this.router.navigateByUrl('/');
      }
    }, 2000);
  }

  isValidAuthData(auth: AuthState){
    if(!auth || !auth.profileId) return false;

    // check if auth is expired
    if(auth.expiredAt){
      const expiredAt = new Date(auth.expiredAt as any);
      const now = new Date();
      if(expiredAt < now) {
        return false;
      }
    }

    return true;
  }

  isLoggedIn(): boolean{
    const auth = this.getAuth();

    if(!auth || !auth.profileId) return false;

    // check if auth is expired
    if(auth.expiredAt){
      const expiredAt = new Date(auth.expiredAt as any);
      const now = new Date();
      if(expiredAt < now) {
        this.handleAuthSessionExpired();
        return false;
      }
    }

    return true;
  }

  onAuthChanged(): Observable<string>{
    return this.getProfileId();
  }

  VerifyResetPasswordCode(request: ApiUserResetPasswordRequestRQ): Observable<ApiResponse> {
    return this.post('ResidentPortalUser/VerifyResetPasswordCode', request);
  }

  getProfileExtend(): Observable<ReadProfileDetailRS> {
    return this.post('ResidentPortalProfile/FullDetail');

  }

  readProfileGender(): Observable<ReadProfileGenderRS> {
    return this.post('ResidentPortalProfile/ReadProfileGender');
  }

  forceLogout(){
    this.clearData();
  }

  clearData(){
    this.localStorageService.setItem(AUTH_KEY, initialState);

    this.setProfileId("");
    this._lastLoginStatus = false;    
    this.stopPingTimer();
  }

  private _isLoggingOut = false;
  logout(userManual = false, callback?: any, delay = 0){
    if(this._isLoggingOut){
      return;
    }
    this._isLoggingOut = true;
    this.revokeToken().subscribe({
      next: (res) => {
        this.handleLogoutResponse(userManual, callback, delay);
      },
      error: (err) => {
        this.handleLogoutResponse(userManual, callback, delay);
      }
    })
  }

  handleLogoutResponse(userManual = false, callback?: any, delay = 0){
    this._isLoggingOut = false;
    this.clearData();
    if(userManual){
      if(this.config.isUsingExternalIdentityServer){
        setTimeout(() => {
          this.forceLogoutFromIDP();
        }, 300);
      }
    }
    else{
      callback && callback(true);
    }
  }

  private _shown409Error = false;
  shouldShow409Error(): boolean{
    return !this._shown409Error;
  }

  show409Error(){
    if(!this.shouldShow409Error()) return;
    this._shown409Error = true;
    this.ui.toastErrorT('api.error.DS0703');
  }

  sessionTimeout(){
    this.ui.toastT('common.error.forced-logout', {duration: TOAST_FOREVER});

    // let's user read force logout message in 2 seconds before logging out
    setTimeout(() => {
      this.logout(true);
    }, 2000);
  }

  signUp(profile: ShellProfile): Observable<ProfileCreateRS>{
    return this.httpClient.post<ProfileCreateRS>(this.baseProfileApiUrl + '/CreateLead', profile);
  }

  changePassword(currentPassword: string, newPassword: string): Observable<ApiResponse> {
    const request = {
      currentPassword: currentPassword,
      newPassword: newPassword,
    };

    return this.post(this.baseProfileApiUrl + `/ChangePassword`, request);
  }

  refreshToken(): Observable<TokenRS> {
    return this.post('WBEAuthentication/refresh-token',true);
  }

  autoRefreshToken(){
    this.refreshToken().subscribe(tokenRes => {
      const now = new Date();
      const expiredAt: any = now.setSeconds(now.getSeconds() + tokenRes.expiresInSeconds);

      const auth = this.getAuth();
      const newAuth: AuthState = {
        ...auth, 
        expiredIn: tokenRes.expiresInSeconds,
        expiredAt: expiredAt,
      }

      this.saveAuthToStorage(newAuth);
    })
  }

  saveAuthToStorage(auth: AuthState){
    this.localStorageService.setItem(AUTH_KEY, auth);
  }

  autoLoginApi(): Observable<TokenRS> {
    return this.post('WBEAuthentication/wbe-auto-login');
  }

  authAutoLogin(queryString?: string): Observable<AuthState>{
    return new Observable((observable) => {
      this.autoLoginApi().subscribe({
        next: (tokenRes) => {
          if (tokenRes.success) {
            let arrPermissions: string[] = [];
  
            if (tokenRes.grantedPermissions) {
              arrPermissions = tokenRes.grantedPermissions.split(';');
            }
  
            const now = new Date();
            const expiredAt: any = now.setSeconds(now.getSeconds() + tokenRes.expiresInSeconds);
  
            const auth: AuthState = {
              isAuthenticated: tokenRes.requireChangePw ? false : true,
              expiredIn: tokenRes.expiresInSeconds,
              expiredAt: expiredAt,
              profileId: tokenRes.profileId,
              userName: tokenRes.userName,
              grantedClaims: arrPermissions,
              requireChangePw: tokenRes.requireChangePw,
              firstName: tokenRes.firstName,
              lastName: tokenRes.lastName,
              // userLogonID : tokenRes.userLogonID
            };

            this.loginSuccess(auth);

            observable.next(auth);
          } else {
            // auto login failed
            observable.next(null);
          }
        },
        error: (err) => {
          observable.next(err);
        }
      })
    })
  }

  // private _keycloakToken: string = null;

  // set keycloakToken(value: string){
  //   this._keycloakToken = value;
  // }

  // get keycloakToken(): string{
  //   return this._keycloakToken;
  // }

  private _isLogging = false;

  isLogging(){
    return this._isLogging;
  }

  captureKeyloakAuth(params?: {redirectTo?: String}): Observable<boolean>{
    return new Observable((observable) => {
      if(!this.config.isUsingExternalIdentityServer) {
        observable.next(false);
        return;
      }

      if(this.isLoggedIn()){
        observable.next(true);
        return;
      }
  
      const isLoggedIn = this.keycloak.isLoggedIn();
      if(isLoggedIn){
        // logged in with keycloak, get token
        this.keycloak.getToken().then( token => {
          if(token){
            // call our API to get auth
            this.idpToken = token;
            this.log.log(`Keycloak token: ${token}`);
            this.processLoginWithIDP().subscribe({
              next: auth => {
                this.processLoginWithIDPSuccess(auth, params);
                observable.next(auth ? true : false);
              },
              error: (err) => {
                this.log.log(`processLoginWithKeycloak err: ${err?.message}, status: ${err?.status}, errorCode: ${err?.errorCode}`);
                observable.next(false);
                if(err && err.status === 401){
                  setTimeout(() => {
                    this.forceLogoutFromKeycloak();
                  }, 2000);
                }
              }
            });
          }
          else{
            this.log.log(`Keycloak token is empty`);
          }
        })
        .catch(err => {
          observable.next(false);
        })
      }
      else{
        this.log.log(`Keycloak is not logged in`);
      }
    });
  }

  generateUUID(): string{
    return uuid.v4();
  }

  loginWithIDP(returnURL?: string){
    if(this.config.externalAuthorizationProvider === 'Keycloak'){
      this.loginWithKeycloak(returnURL);
      return;
    }

    if(this.config.externalAuthorizationProvider === 'Okta'){
      this.loginWithOkta(returnURL);
      return;
    }
    this.log.log(`Do not support externalAuthorizationProvider: ${this.config.externalAuthorizationProvider}`);
  }

  private loginWithOkta(returnURL?: string){
    let redirectURL = returnURL || window.location.href;
    if(!redirectURL.includes('lang=')){
      if(redirectURL.includes('?')){
        redirectURL+= `&lang=` + this.language.getCurrentLanguageCode()
      }
      else{
        redirectURL+= `?lang=` + this.language.getCurrentLanguageCode()
      }
    }
    else{
      redirectURL = redirectURL.replace(/lang=[a-z]{2}/i, `lang=${this.language.getCurrentLanguageCode()}`);
    }

    if(redirectURL){
      const baseEle = document.querySelector('base');
      if(baseEle){
        const basePath = baseEle.getAttribute('href');
        let path = redirectURL;
        if(basePath && basePath !== '/'){
          path = path.replace(basePath, '');
        }
        path = path.replace(window.location.origin, '');
        this.localStorageService.setItem(`REDIRECT_URL`, path);
      }

    }

    this.oktaService.loginWithRedirect();
  }

  private loginWithKeycloak(returnURL?: string){
    let redirectURL = returnURL || window.location.href;
    if(!redirectURL.includes('lang=')){
      if(redirectURL.includes('?')){
        redirectURL+= `&lang=` + this.language.getCurrentLanguageCode()
      }
      else{
        redirectURL+= `?lang=` + this.language.getCurrentLanguageCode()
      }
    }
    else{
      redirectURL = redirectURL.replace(/lang=[a-z]{2}/i, `lang=${this.language.getCurrentLanguageCode()}`);
    }

    this.keycloak.login({
      redirectUri: redirectURL
    });
  }

  forceLogoutFromKeycloak(){
    this.keycloak.logout();
  }

  private forceLogoutFromIDP(){
    if(this.config.externalAuthorizationProvider === 'Keycloak'){
      return this.forceLogoutFromKeycloak();
    }

    if(this.config.externalAuthorizationProvider === 'Okta'){
      return this.forceLogoutFromOkta();
    }
  }

  rootURL(): string{
    let tag = document.querySelector('base');
    if(tag){
      let url = tag.href;
      if(url !== '' && url !== '/'){
        return url;
      }
    }
    return window.location.origin;
  }

  private forceLogoutFromOkta(){
    this.oktaService.logout({logoutParams: {returnTo: this.rootURL()}});
  }

  redirectToRSP(redirectTo?: string){
    const rspUrl = window.location.href.indexOf('localhost') !== -1 ? 'https://localhost:4202': this.config.RSPSite;

    const redirectURL = rspUrl + `/redirect-login/${this.generateUUID()}?${redirectTo ? `redirect_to=${redirectTo}` : ''}`;

    if(this.config.externalAuthorizationProvider === 'Keycloak'){
      this.keycloak.login({
        redirectUri: redirectURL
      });
      return;
    } 

    window.location.href = redirectURL;
  }

  redirectToRSPUploadDocument(){
    this.redirectToRSP('upload-document');
  }

  isUsingExternalIdentityServer(){
    return this.config.isUsingExternalIdentityServer;
  }

  captureIDPAuth(params?: {redirectTo?: String}): Observable<boolean>{
    if(this.config.externalAuthorizationProvider === 'Keycloak'){
      return this.captureKeyloakAuth(params);
    }

    if(this.config.externalAuthorizationProvider === 'Okta'){
      return this.captureOktaAuth(params);
    }

    return new Observable((observable) => {
      setTimeout(() => {
        observable.next(false);
      }, 300);
    });
  }

  private captureOktaAuth(params?: {redirectTo?: String}): Observable<boolean>{
    return new Observable((observable) => {
      if(!this.config.isUsingExternalIdentityServer) {
        observable.next(false);
        return;
      }

      this.oktaService.isAuthenticated$.subscribe(isLoggedIn => {
        if(isLoggedIn){
          // logged in with keycloak, get token
          this.oktaService.getAccessTokenSilently().subscribe( token => {
            if(token){
              // call our API to get auth
              this.idpToken = token;
  
              const path = this.localStorageService.getItem('REDIRECT_URL');
              // if(path){
              //   this.log.log(`captureOktaAuth Redirect to: ${path}`);
              //   this.router.navigateByUrl(path);
              // }
  
              this.processLoginWithIDP().subscribe({
                next: (auth) => {
                  this.processLoginWithIDPSuccess(auth, params);
                  observable.next(auth ? true : false);
                  if (path) {
                    this.log.log(`captureOktaAuth Redirect to: ${path}`);
                    this.router.navigateByUrl(path);
                  }
                },
                error: (err) => {
                  this.log.log(`captureOktaAuth err: ${err?.message}, status: ${err?.status}, errorCode: ${err?.errorCode}`);
                  observable.next(false);
                  if(err && err.status === 401){
                    setTimeout(() => {
                      this.forceLogoutFromOkta();
                    }, 2000);
                  }
                }
              });
            }
            else{
              observable.next(false);
            }
          })
        }
        else{
          observable.next(false);
        }
      })
    })
  }

  private processLoginWithIDPSuccess(auth: AuthState, params?: {redirectTo?: String}){
    if(!auth){
      return;
    }

    let stop = false;
    if(params && params.redirectTo){
      
    }
  }

  processLoginWithIDP(): Observable<AuthState>{
    return new Observable((observable) => {
      const body = {
        isAdminUser: false,
        username: this.generateUUID(),
        password: this.generateUUID()
      };
      this._isLogging = true;
      this.post<TokenRS>(this.baseAuthApiUrl + `/token`, body).subscribe({
        next: (tokenRes) => {
          this._isLogging = false;
          this.handleApiLoginSuccess(observable, tokenRes);
        },
        error: (err) => {
          this._isLogging = false;
          observable.error(err);
        }
      });
    })
  }

  onActivateAccount(): Observable<any>{
    return this._activateAccountSB.asObservable();
  }

  activateAccount(){
    this._activateAccountSB.next(null);
  }
}
